/**
 * Medical specialty catalogue and doctor-record normalisation.
 *
 * The category list is the `defaultCategories` array from app.js, kept as the
 * offline fallback for when GET /api/categories has not resolved yet. The
 * Arabic-name -> id map is the legacy migration table, still needed because
 * older doctor rows stored the display name in `categoryId`.
 *
 * `normalizeDoctor` replaces four separate ad-hoc mappings of the same API
 * response (loadData, handleDoctorLogin, renderDoctorDashboard_v2 and the smart
 * refresh re-hydrator), which had drifted enough that a doctor's price could
 * appear differently depending on which one ran last.
 */

export const DEFAULT_CATEGORIES = [
  { id: 'internal', name: 'باطنة', icon: 'stethoscope', count: 0 },
  { id: 'ortho', name: 'عظام', icon: 'bone', count: 0 },
  { id: 'dental', name: 'أسنان', icon: 'smile', count: 0 },
  { id: 'cardio', name: 'قلب وأوعية دموية', icon: 'heart-pulse', count: 0 },
  { id: 'derma', name: 'جلدية وتجميل', icon: 'sparkles', count: 0 },
  { id: 'pedatric', name: 'أطفال', icon: 'baby', count: 0 },
  { id: 'gyneco', name: 'نساء وتوليد', icon: 'person-standing', count: 0 },
  { id: 'neuro', name: 'مخ وأعصاب', icon: 'brain', count: 0 },
  { id: 'eye', name: 'عيون', icon: 'eye', count: 0 },
  { id: 'ent', name: 'أنف وأذن وحنجرة', icon: 'ear', count: 0 },
  { id: 'surgery', name: 'جراحة عامة', icon: 'scissors', count: 0 },
  { id: 'physio', name: 'علاج طبيعي', icon: 'activity', count: 0 },
  { id: 'nutrition', name: 'تغذية علاجية', icon: 'apple', count: 0 },
  { id: 'psych', name: 'نفسية وعصبية', icon: 'lightbulb', count: 0 },
  { id: 'chest', name: 'أمراض صدرية', icon: 'wind', count: 0 },
  { id: 'urology', name: 'مسالك بولية', icon: 'droplet', count: 0 },
  { id: 'gastro', name: 'جهاز هضمي', icon: 'utensils', count: 0 },
  { id: 'nephrology', name: 'أطوار كلى', icon: 'filter', count: 0 },
  { id: 'oncology', name: 'أورام', icon: 'plus-circle', count: 0 },
  { id: 'rheuma', name: 'روماتيزم ومناعة', icon: 'shield-plus', count: 0 },
  { id: 'labs', name: 'تحاليل طبية', icon: 'microscope', count: 0 },
  { id: 'radiology', name: 'أشعة', icon: 'scan', count: 0 },
];

/**
 * A colour per specialty.
 *
 * The legacy grid painted every tile the same cyan, which made twenty-two
 * specialties a wall of identical squares. Giving each its own hue is what makes
 * the grid scannable — you learn where "أطفال" sits by its colour long before
 * you read the label.
 *
 * Values are `[r, g, b]` so the tile can build both its tint and its hover glow
 * from one number, at whatever opacity each needs.
 */
export const CATEGORY_RGB = {
  internal: [16, 185, 129],
  ortho: [59, 130, 246],
  dental: [168, 85, 247],
  cardio: [245, 158, 11],
  derma: [244, 63, 94],
  pedatric: [139, 92, 246],
  gyneco: [236, 72, 153],
  neuro: [59, 130, 246],
  eye: [16, 185, 129],
  ent: [14, 165, 233],
  surgery: [168, 85, 247],
  physio: [250, 204, 21],
  nutrition: [239, 68, 68],
  psych: [129, 140, 248],
  chest: [45, 212, 191],
  urology: [56, 189, 248],
  gastro: [34, 197, 94],
  nephrology: [6, 182, 212],
  oncology: [192, 132, 252],
  rheuma: [251, 146, 60],
  labs: [148, 163, 184],
  radiology: [125, 211, 252],
};

/** Cyan, matching the rest of the chrome, for a specialty not in the table. */
const FALLBACK_RGB = [6, 182, 212];

/**
 * Everything a category tile needs to paint itself.
 *
 * `glow` and `glowSoft` feed the CSS custom properties the `.category-card`
 * hover rule reads, so each tile lights up in its own colour rather than all of
 * them flashing cyan.
 */
export function categoryAccent(categoryId) {
  const rgb = CATEGORY_RGB[normalizeCategoryId(categoryId)] || FALLBACK_RGB;
  const [r, g, b] = rgb;
  return {
    rgb,
    color: `rgb(${r}, ${g}, ${b})`,
    tint: `rgba(${r}, ${g}, ${b}, 0.12)`,
    border: `rgba(${r}, ${g}, ${b}, 0.25)`,
    glow: `rgba(${r}, ${g}, ${b}, 0.55)`,
    glowSoft: `rgba(${r}, ${g}, ${b}, 0.25)`,
  };
}

/**
 * Legacy migration map. Note `أطفال` maps to `pediatric` here but the catalogue
 * id is `pedatric` (a typo that shipped); both are accepted on lookup so neither
 * old nor new records are lost.
 */
export const CATEGORY_NAME_TO_ID = {
  باطنة: 'internal',
  عظام: 'ortho',
  أسنان: 'dental',
  'قلب وأوعية دموية': 'cardio',
  'جلدية وتجميل': 'derma',
  أطفال: 'pedatric',
  'نساء وتوليد': 'gyneco',
  'مخ وأعصاب': 'neuro',
  عيون: 'eye',
  'أنف وأذن وحنجرة': 'ent',
  'جراحة عامة': 'surgery',
  'علاج طبيعي': 'physio',
  'تغذية علاجية': 'nutrition',
  'نفسية وعصبية': 'psych',
  'أمراض صدرية': 'chest',
  'مسالك بولية': 'urology',
  'جهاز هضمي': 'gastro',
  'أطوار كلى': 'nephrology',
  أورام: 'oncology',
  'روماتيزم ومناعة': 'rheuma',
  'تحاليل طبية': 'labs',
  أشعة: 'radiology',
};

/** Aliases for ids that changed spelling between versions. */
const CATEGORY_ID_ALIASES = { pediatric: 'pedatric' };

/** Coerce whatever a record holds into a catalogue id. */
export function normalizeCategoryId(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (CATEGORY_NAME_TO_ID[raw]) return CATEGORY_NAME_TO_ID[raw];
  return CATEGORY_ID_ALIASES[raw] || raw;
}

/** Display name for a category id, falling back to the id itself. */
export function categoryName(categoryId, categories = DEFAULT_CATEGORIES) {
  const id = normalizeCategoryId(categoryId);
  return categories.find((c) => c.id === id)?.name || id || '';
}

/** Lucide icon name for a category id. */
export function categoryIcon(categoryId, categories = DEFAULT_CATEGORIES) {
  const id = normalizeCategoryId(categoryId);
  return categories.find((c) => c.id === id)?.icon || 'stethoscope';
}

/**
 * Merge the API's category list over the built-in defaults so a category the
 * backend has not seeded still renders instead of vanishing from the grid.
 */
export function mergeCategories(apiCategories = []) {
  if (!Array.isArray(apiCategories) || apiCategories.length === 0) {
    return DEFAULT_CATEGORIES.map((c) => ({ ...c }));
  }
  const merged = apiCategories.map((c) => ({ ...c, id: normalizeCategoryId(c.id) }));
  for (const fallback of DEFAULT_CATEGORIES) {
    if (!merged.some((c) => c.id === fallback.id)) merged.push({ ...fallback });
  }
  return merged;
}

/** Recompute `count` per category from a doctor list. */
export function withDoctorCounts(categories, doctors = []) {
  const counts = new Map();
  for (const doctor of doctors) {
    const id = normalizeCategoryId(doctor.category ?? doctor.categoryId);
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  return categories.map((c) => ({ ...c, count: counts.get(c.id) || 0 }));
}

/** Deterministic placeholder avatar, same service the legacy app used. */
export const avatarFor = (name) =>
  `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(name || 'doctor')}`;

/**
 * Flatten a doctor row from GET /api/doctors or /api/doctors/profile into the
 * flat shape the UI works with.
 *
 * The backend nests configuration under `DoctorProfile` and clinics under
 * `Clinics`; every legacy renderer un-nested it by hand, differently.
 */
export function normalizeDoctor(apiDoctor) {
  if (!apiDoctor) return null;
  const profile = apiDoctor.DoctorProfile || {};

  return {
    id: apiDoctor.id,
    name: apiDoctor.name,
    username: apiDoctor.username,
    email: apiDoctor.email ?? null,
    phone: apiDoctor.phone ?? null,
    status: apiDoctor.status || 'active',
    suspended: apiDoctor.status === 'suspended',

    category: normalizeCategoryId(profile.categoryId) || 'internal',
    categoryName: profile.Category?.name || categoryName(profile.categoryId),
    title: profile.title || '',

    price_exam: Number(profile.priceExam ?? 500),
    price_followup: Number(profile.priceFollowup ?? 200),

    image: profile.image || apiDoctor.picture || avatarFor(apiDoctor.name),
    logo: profile.logo || '',

    hasNightDiscount: Boolean(profile.hasNightDiscount),
    nightDiscountPercent: Number(profile.nightDiscountPercent ?? 25),
    hasFirstVisitDiscount: Boolean(profile.hasFirstVisitDiscount),
    firstVisitDiscountPercent: Number(profile.firstVisitDiscountPercent ?? 10),

    prescriptionStyle: profile.prescriptionStyle || 'wave-top',
    printColors: {
      primary: profile.printPrimaryColor || '#06b6d4',
      secondary: profile.printSecondaryColor || '#a855f7',
    },

    clinics: Array.isArray(apiDoctor.Clinics) ? apiDoctor.Clinics : [],
    offers: Array.isArray(apiDoctor.Offers) ? apiDoctor.Offers : [],

    // Day-level schedule overrides. Both calendars read these to mark a day, so
    // they are flattened to the shapes the grids expect: extra hours as objects
    // keyed by date, cancelled days as a plain list of date strings.
    extraHours: Array.isArray(apiDoctor.DoctorExtraHours)
      ? apiDoctor.DoctorExtraHours.map(normalizeExtraHours)
      : [],
    cancelledDays: Array.isArray(apiDoctor.DoctorCancelledDays)
      ? apiDoctor.DoctorCancelledDays.map((entry) => dateOnly(entry?.date)).filter(Boolean)
      : [],
  };
}

/** Postgres DATEONLY comes back as 'YYYY-MM-DD'; a DATE would carry a time. */
const dateOnly = (value) => (value ? String(value).slice(0, 10) : null);

/** One evening block, in the shape the calendars and the day modal read. */
export function normalizeExtraHours(entry) {
  if (!entry) return null;
  return {
    id: entry.id ?? null,
    date: dateOnly(entry.date),
    clinicId: entry.clinicId ?? null,
    slots: Array.isArray(entry.slots) ? entry.slots : [],
    endTime: entry.endTime || null,
    duration: entry.duration ?? null,
    hasNightOffer: Boolean(entry.hasNightOffer),
    nightOfferDiscount: Number(entry.nightOfferDiscount) || 0,
  };
}

/** Map a whole API list, dropping rows without a doctor profile. */
export function normalizeDoctors(apiDoctors = []) {
  return (apiDoctors || [])
    .filter((d) => d && (d.DoctorProfile || d.id))
    .map(normalizeDoctor)
    .filter(Boolean);
}

/**
 * Flatten a team member row from GET /api/team/members, matching the shape the
 * legacy `loadData` produced.
 */
export function normalizeTeamMember(apiMember) {
  if (!apiMember) return null;
  const profile = apiMember.TeamMemberProfile || {};

  return {
    id: apiMember.id,
    name: apiMember.name,
    username: apiMember.username,
    status: apiMember.status,
    contact: apiMember.phone || '',
    roles: Array.isArray(profile.jobs) ? profile.jobs : [],
    capacityStatus: profile.capacityStatus || 'available',
    team_member_code: profile.team_member_code || null,
    assignedProjects: (apiMember.AssignedProjects || []).map((p) => ({
      id: p.id,
      status: p.status,
      doctorName: p.User?.name || 'غير محدد',
    })),
    assignedDoctors: (apiMember.AssignedDoctors || []).map((d) => d.id),
  };
}

export const normalizeTeamMembers = (members = []) =>
  (members || []).map(normalizeTeamMember).filter(Boolean);
